import api from "./api";

/*
 * ================================================================
 * Service unique du parcours Agreement → Checkout → Paiement → Commande → Livraison
 * ================================================================
 *
 * Le frontend n'envoie que des choix (type de livraison, devis choisi,
 * méthode, opérateur, téléphone). Montants, pays du payeur, provider et
 * statuts sont déterminés par le backend. L'Agreement n'est jamais modifié ici.
 */

const requireIds = (ids, message) => {
    if (Object.values(ids).some(value => !value)) {
        throw new Error(message);
    }
};

const TransactionService = {

    async getBuyerAgreements() {
        const response = await api.get("/shipping/agreement");
        return response.data;
    },

    /**
     * Avancement post-Agreement de chaque accord de l'utilisateur (lecture seule) :
     * inquiryId, checkout, dernier paiement, commande, livraison.
     */
    async getPurchases() {
        const response = await api.get("/marketplace/purchases");
        return response.data ?? [];
    },

    /**
     * Indicateurs vendeur de l'utilisateur connecté : produits publiés, commandes
     * reçues et paiements reçus par devise ([{ currency, amount }]).
     */
    async getSellerSummary() {
        const response = await api.get("/marketplace/seller/summary");
        return response.data;
    },

    async getAgreement(inquiryId, agreementId) {
        requireIds({ inquiryId, agreementId }, "inquiryId et agreementId sont requis");
        const response = await api.get(`/shipping/agreement/${inquiryId}/agreement/${agreementId}`);
        return response.data;
    },

    /**
     * Chiffre un mode de livraison. `address` est obligatoire pour la livraison
     * ASSIGANTO ou partenaire : { country, city, district, street, landmark,
     * additionalInformation, contactPhone }.
     * Chaque option renvoyée porte son récapitulatif (produits, livraison, total)
     * calculé par le backend.
     */
    async getDeliveryQuotes(inquiryId, { agreementId, deliveryType, address }) {
        requireIds({ inquiryId, agreementId, deliveryType }, "inquiryId, agreementId et deliveryType sont requis");
        const response = await api.post(`/marketplace/inquiry/${inquiryId}/delivery/quotes`, {
            agreementId,
            deliveryType,
            address: address || null
        });
        return response.data;
    },

    /** Suivi de la livraison de la commande, ou null si elle n'existe pas encore. */
    async getDelivery(inquiryId, agreementId) {
        requireIds({ inquiryId, agreementId }, "inquiryId et agreementId sont requis");
        const response = await api.get(`/marketplace/inquiry/${inquiryId}/delivery`, {
            params: { agreementId }
        });
        return response.status === 204 ? null : response.data;
    },

    /**
     * Moyens de paiement du pays d'activité (déterminé par le backend via le JWT),
     * filtrés sur la devise de l'accord. `paymentServiceAvailable === false` :
     * le service de paiement est injoignable.
     */
    async getPaymentOptions(currency) {
        const response = await api.get("/marketplace/payment/options", {
            params: currency ? { currency } : undefined
        });
        return response.data;
    },

    /**
     * Confirme le checkout : { agreementId, deliveryType, deliveryOptionId, paymentMethod, mobileOperator }.
     * Les montants (produit + livraison = total) sont calculés et figés par le backend.
     */
    async confirmCheckout(inquiryId, { agreementId, deliveryType, deliveryOptionId, paymentMethod, mobileOperator }) {
        requireIds({ inquiryId, agreementId }, "inquiryId et agreementId sont requis");
        const response = await api.put(`/marketplace/inquiry/${inquiryId}/checkout`, {
            agreementId,
            deliveryType,
            deliveryOptionId: deliveryOptionId || null,
            paymentMethod,
            mobileOperator: mobileOperator || null
        });
        return response.data;
    },

    /** Checkout de l'accord, ou null s'il n'a pas encore été confirmé. */
    async getCheckout(inquiryId, agreementId) {
        requireIds({ inquiryId, agreementId }, "inquiryId et agreementId sont requis");
        const response = await api.get(`/marketplace/inquiry/${inquiryId}/checkout`, {
            params: { agreementId }
        });
        return response.status === 204 ? null : response.data;
    },

    /**
     * Initie (ou reprend) le paiement du checkout confirmé. Seul le numéro
     * Mobile Money est transmis ; la même idempotencyKey reprend la même tentative.
     */
    async initiatePayment(inquiryId, { agreementId, phoneNumber }, idempotencyKey) {
        requireIds({ inquiryId, agreementId, idempotencyKey }, "inquiryId, agreementId et idempotencyKey sont requis");
        const response = await api.post(
            `/marketplace/inquiry/${inquiryId}/payment`,
            { agreementId, phoneNumber: phoneNumber || null },
            { headers: { "Idempotency-Key": idempotencyKey } }
        );
        return response.data;
    },

    /** Commande de l'accord (créée après confirmation du paiement), ou null. */
    async getOrder(inquiryId, agreementId) {
        requireIds({ inquiryId, agreementId }, "inquiryId et agreementId sont requis");
        const response = await api.get(`/marketplace/inquiry/${inquiryId}/order`, {
            params: { agreementId }
        });
        return response.status === 204 ? null : response.data;
    },

    async getPayment(inquiryId, paymentId) {
        requireIds({ inquiryId, paymentId }, "inquiryId et paymentId sont requis");
        const response = await api.get(`/marketplace/inquiry/${inquiryId}/payment/${paymentId}`);
        return response.data;
    },

    /** Dernier paiement de l'accord, ou null s'il n'y en a pas encore. */
    async getLatestPayment(inquiryId, agreementId) {
        requireIds({ inquiryId, agreementId }, "inquiryId et agreementId sont requis");
        const response = await api.get(`/marketplace/inquiry/${inquiryId}/payment`, {
            params: { agreementId }
        });
        return response.status === 204 ? null : response.data;
    }
};

export default TransactionService;
